'use client';

import { useCallback, useEffect, useRef } from 'react';

/* Hysteresis, so a single threshold can't flip the state back and forth while
   the answer grows under the visitor. Following only resumes when they are
   genuinely at the end, and only stops once they are clearly away from it —
   in between, whatever they last did stands. */
const RESUME_WITHIN = 8;
const PAUSE_BEYOND = 96;

/** Time after a finger lifts before we touch the scroll position again, so the
 *  browser's momentum scroll gets to finish on its own. */
const MOMENTUM_REST_MS = 150;

/** @param {HTMLElement} el */
const distanceFromBottom = (el) => el.scrollHeight - el.scrollTop - el.clientHeight;

/**
 * Keep a scroller pinned to the bottom while an answer streams in — without
 * ever fighting the visitor for the scroll position.
 *
 * Two rules keep it from stuttering. First, intent beats position: a wheel tick
 * or an arrow key upwards stops the follow on the spot, rather than waiting for
 * a scroll event that our own scrolling would race. Second, while a finger (or
 * the scrollbar thumb) is down — and for a moment after it lifts, while
 * momentum plays out — the visitor owns the position and we write nothing.
 *
 * Writes are coalesced to one per animation frame, so a burst of streamed
 * tokens moves the scroller once per paint instead of a dozen times.
 *
 * @param {{ scrollerRef: import('react').RefObject<HTMLElement>, contentRef?: import('react').RefObject<HTMLElement>, active?: boolean }} options
 */
export default function useFollowOutput({ scrollerRef, contentRef, active = true }) {
  const followRef = useRef(true);
  const draggingRef = useRef(false);
  const restUntilRef = useRef(0);
  const relayoutUntilRef = useRef(0);
  const writtenTopRef = useRef(-1);
  const frameRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    if (!followRef.current || draggingRef.current) return;
    if (performance.now() < restUntilRef.current) return;

    // Re-book rather than skip: a frame that never gets painted (a backgrounded
    // tab) would otherwise leave a pending id behind and block every later call.
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = 0;
      const scroller = scrollerRef.current;
      if (!scroller || !followRef.current || draggingRef.current) return;

      const target = scroller.scrollHeight - scroller.clientHeight;
      // Already there: writing anyway would cancel an in-flight smooth scroll.
      if (target - scroller.scrollTop < 1) return;
      scroller.scrollTop = target;
      // Remember where we put it, so the scroll event this fires isn't mistaken
      // for the visitor moving away — by the time it arrives the answer has
      // usually grown again, and the distance alone reads exactly like a scroll up.
      writtenTopRef.current = scroller.scrollTop;
    });
  }, [scrollerRef]);

  /** Jump back to the live end — for a new question, where following is implied. */
  const resume = useCallback(() => {
    followRef.current = true;
    restUntilRef.current = 0;
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!active || !scroller) return undefined;

    const stopFollowing = () => {
      followRef.current = false;
    };

    const onScroll = () => {
      // Our own scrolling, echoed back: the position is still exactly where we
      // put it, so nothing about the visitor's intent has changed.
      if (scroller.scrollTop === writtenTopRef.current) return;
      const distance = distanceFromBottom(scroller);
      if (distance <= RESUME_WITHIN) followRef.current = true;
      // A relayout — a keyboard opening, a rotation — moves the bottom away by
      // the height it took, and the scroll event that follows would otherwise
      // read exactly like the visitor scrolling up. Only they get to stop it.
      else if (distance > PAUSE_BEYOND && performance.now() >= relayoutUntilRef.current) followRef.current = false;
    };
    const onWheel = (event) => {
      if (event.deltaY < 0) stopFollowing();
    };
    const onKeyDown = (event) => {
      if (event.key === 'ArrowUp' || event.key === 'PageUp' || event.key === 'Home') stopFollowing();
    };
    const onDragStart = () => {
      draggingRef.current = true;
    };
    // Only the drag flag is cleared here: the scroll events that momentum keeps
    // firing decide, from the position, whether we are following again.
    const onDragEnd = () => {
      draggingRef.current = false;
      restUntilRef.current = performance.now() + MOMENTUM_REST_MS;
    };

    scroller.addEventListener('scroll', onScroll, { passive: true });
    scroller.addEventListener('wheel', onWheel, { passive: true });
    scroller.addEventListener('keydown', onKeyDown);
    scroller.addEventListener('touchstart', onDragStart, { passive: true });
    scroller.addEventListener('touchend', onDragEnd, { passive: true });
    scroller.addEventListener('touchcancel', onDragEnd, { passive: true });
    scroller.addEventListener('mousedown', onDragStart);
    window.addEventListener('mouseup', onDragEnd);

    // Opening the assistant always lands on the latest exchange: the visitor
    // came back to what they just asked, not to where they left the scroll.
    followRef.current = true;
    scrollToBottom();

    // A ResizeObserver catches everything that moves the end of the
    // conversation — streamed text, the blurb cards, images that finish
    // loading, and the scroller itself shrinking around a keyboard — where an
    // effect on the message list alone would miss all but the first.
    const onResize = (entries) => {
      if (entries.some((entry) => entry.target === scroller)) relayoutUntilRef.current = performance.now() + 400;
      scrollToBottom();
    };

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    if (observer) {
      observer.observe(scroller);
      if (contentRef?.current) observer.observe(contentRef.current);
    }

    return () => {
      scroller.removeEventListener('scroll', onScroll);
      scroller.removeEventListener('wheel', onWheel);
      scroller.removeEventListener('keydown', onKeyDown);
      scroller.removeEventListener('touchstart', onDragStart);
      scroller.removeEventListener('touchend', onDragEnd);
      scroller.removeEventListener('touchcancel', onDragEnd);
      scroller.removeEventListener('mousedown', onDragStart);
      window.removeEventListener('mouseup', onDragEnd);
      observer?.disconnect();
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
    };
  }, [active, contentRef, scrollToBottom, scrollerRef]);

  return { scrollToBottom, resume };
}
